/**
 * Bell icon + dropdown showing tenant-admin advisory notifications.
 *
 * Source: GET /api/notifications/, polled every 5 minutes (notifications are
 * derived from current DB state; staleness is measured in scan cadence, not
 * milliseconds — the nightly worker writes the underlying state once a day).
 *
 * Dismissals: stored per-browser in localStorage. Underlying-state changes
 * cause the synthetic id to differ (see backend notification_views.py), so a
 * dismissed warning resurfaces when its severity escalates to error.
 */

import { Badge, Button, Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";

import { type Notification } from "@/api/notifications";
import { useNotifications } from "@/lib/useNotifications";

import { SeverityBadge } from "./SeverityBadge";

const POLL_INTERVAL_MS = 5 * 60_000;

/** Coarse "how long ago", matching the dashboard's job list. Absolute time is
 *  carried in the title attribute for anyone who needs the exact moment. */
function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

export function NotificationBell() {
  const { items: visible, dismiss, dismissAll } = useNotifications({
    refetchInterval: POLL_INTERVAL_MS,
  });

  const unread = visible.length;
  const hasError = visible.some((n) => n.severity === "error");

  return (
    <Dropdown align="end" autoClose="outside">
      <Dropdown.Toggle
        as={Button}
        variant="outline-secondary"
        size="sm"
        id="notifications-bell"
        aria-label={`Notifications: ${unread} unread`}
        className="position-relative"
      >
        <span aria-hidden>🔔</span>
        {unread > 0 && (
          <Badge
            bg={hasError ? "danger" : "warning"}
            pill
            className="position-absolute top-0 start-100 translate-middle"
            style={{ fontSize: "0.65rem" }}
          >
            {unread > 9 ? "9+" : unread}
            <span className="visually-hidden">unread notifications</span>
          </Badge>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu style={{ minWidth: "20rem", maxWidth: "26rem" }}>
        <Dropdown.Header className="d-flex justify-content-between align-items-center gap-2">
          <span>Notifications</span>
          {visible.length > 1 && (
            // Only offered when there is more than one to clear — with a
            // single item the per-row × is already the shorter path.
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-none small"
              onClick={() => dismissAll(visible.map((n) => n.id))}
            >
              Dismiss all
            </Button>
          )}
        </Dropdown.Header>
        {visible.length === 0 ? (
          <Dropdown.ItemText className="text-muted small">
            All clear — no advisory notifications.
          </Dropdown.ItemText>
        ) : (
          visible.map((n) => (
            <NotificationItem key={n.id} n={n} onDismiss={() => dismiss(n.id)} />
          ))
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function NotificationItem({
  n,
  onDismiss,
}: {
  n: Notification;
  onDismiss: () => void;
}) {
  return (
    <div className="px-3 py-2 border-top">
      <div className="d-flex justify-content-between align-items-start">
        <div className="d-flex gap-2">
          <SeverityBadge severity={n.severity} />
          <strong className="small">{n.title}</strong>
        </div>
        <Button
          variant="link"
          size="sm"
          className="text-muted py-0 px-1"
          onClick={onDismiss}
          aria-label={`Dismiss ${n.title}`}
          title="Dismiss"
        >
          ×
        </Button>
      </div>
      <div className="small text-muted mt-1">{n.message}</div>
      {/* Same reason as the dashboard card: a failed order lives here for a
          week, and undated it reads as having just happened. */}
      {n.occurred_at && (
        <div
          className="small text-muted mt-1"
          title={new Date(n.occurred_at).toLocaleString()}
        >
          {relativeTime(n.occurred_at)}
        </div>
      )}
      {n.action_url && n.action_label && (
        <div className="mt-2">
          {/* react-router Link inside a btn-styled wrapper — Bootstrap Button
              with `href` renders as <button> (no navigation), so we drop down
              to a plain <a> with the .btn classes for proper SPA routing. */}
          <Link
            to={n.action_url}
            className="btn btn-outline-primary btn-sm"
            role="link"
          >
            {n.action_label}
          </Link>
        </div>
      )}
    </div>
  );
}
